/**
 * Szám- és pénzformázó helperek.
 *
 * Pure modul — semmilyen runtime függőség (i18next, react), így a BE is
 * használhatja (pl. PDF/CSV BE-oldali generálásnál).
 *
 * A `NumberFormat` objektum az ország `pta_core.countries.number_format` JSONB-jeből jön:
 *   { decimal: ',', thousand: ' ', currencyPosition: 'after' }
 *
 * A `formatMoney` a `currencySymbol` alapján (pl. 'Ft', '€') rakja a szám mögé vagy elé.
 * Ha symbol == null, csak a szám jelenik meg.
 *
 * `minorUnit` (HUF: 0, EUR: 2, JPY: 0): hány tizedesjegy jelenjen meg minimum.
 */

export interface NumberFormat {
  decimal: string;
  thousand: string;
  currencyPosition: 'before' | 'after';
}

export const DEFAULT_NUMBER_FORMAT: NumberFormat = {
  decimal: ',',
  thousand: ' ',
  currencyPosition: 'after',
};

/**
 * NUMERIC string vagy number → formázott string ezres-elválasztóval.
 *
 * - decimals: ha megadva, pontosan ennyi tizedesjegyet mutat (toFixed-szel).
 *   Ha nincs megadva, az input `Number()`-elt értékéből a tartalmat tartja
 *   meg (pl. '365.50' → '365,5'; '365' → '365').
 */
export function formatNumber(
  value: string | number,
  fmt: NumberFormat = DEFAULT_NUMBER_FORMAT,
  decimals?: number,
): string {
  const num = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(num)) return String(value);

  let raw: string;
  if (decimals !== undefined) {
    raw = num.toFixed(decimals);
  } else {
    raw = String(num);
  }

  const negative = raw.startsWith('-');
  if (negative) raw = raw.slice(1);
  const [intPart, decPart] = raw.split('.');

  const intWithThousands = intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousand);

  let out = intWithThousands;
  if (decPart !== undefined && decPart.length > 0) {
    out += fmt.decimal + decPart;
  }
  return negative ? '-' + out : out;
}

/**
 * Pénzformázás — szám + currency symbol a megfelelő pozíción.
 *
 * Default decimals: a `minorUnit` (HUF: 0, EUR: 2). Ha minorUnit nincs megadva,
 * a szám hosszát követjük (toFixed nélkül).
 */
export function formatMoney(
  value: string | number,
  fmt: NumberFormat = DEFAULT_NUMBER_FORMAT,
  currencySymbol?: string | null,
  minorUnit?: number,
): string {
  const numStr = formatNumber(value, fmt, minorUnit);
  if (!currencySymbol) return numStr;
  return fmt.currencyPosition === 'before'
    ? `${currencySymbol} ${numStr}`
    : `${numStr} ${currencySymbol}`;
}

/**
 * Elavult: a régi `formatNumericString` helyettesítője.
 * NUMERIC(n,m) string trailing-zéróit levágja — backwards-compat helper,
 * hogy a régi hívók kódja ne törjön addig, amíg át nem állítódnak `formatMoney`-re.
 */
export function formatNumericString(raw: string): string {
  if (!raw.includes('.')) return raw;
  const trimmed = raw.replace(/\.?0+$/, '');
  return trimmed === '' || trimmed === '-' ? '0' : trimmed;
}
